// src/components/shared/CallModal.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import MaskedInput from './MaskedInput';
import Input from './Input';
import { FaPhoneAlt, FaSpinner, FaUser } from 'react-icons/fa';
import { initiateClickToCall } from '../../services/communicationService';

const CallDetails = styled.div`
  margin-bottom: ${props => props.theme.spacing(3)};
  padding: ${props => props.theme.spacing(2)};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
`;

const CallInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const PhoneNumber = styled.span`
  font-size: ${props => props.theme.typography.fontSize.large};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const CustomerName = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.medium};
`;

const CallFlow = styled.div`
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing(3)};
  border-radius: ${props => props.theme.borderRadius};
  margin: ${props => props.theme.spacing(3)} 0;
`;

const FlowTitle = styled.h4`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 ${props => props.theme.spacing(2)} 0;
  font-size: ${props => props.theme.typography.fontSize.medium};
`;

const FlowStep = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.small};
  
  &:before {
    content: '${props => props.step}';
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.black};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(3)};
`;

const CallButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.black};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(3)};
  cursor: pointer;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: all 0.2s ease;
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 1.1rem;
  }
`;

const CancelButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.inputGray};
  color: ${props => props.theme.colors.textPrimary};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(3)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.surface};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  background: ${props => props.theme.colors.error}22;
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};
  margin-top: ${props => props.theme.spacing(2)};
  font-size: ${props => props.theme.typography.fontSize.small};
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.success};
  background: ${props => props.theme.colors.success}22;
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};
  margin-top: ${props => props.theme.spacing(2)};
  font-size: ${props => props.theme.typography.fontSize.small};
`;

const RememberChoice = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  margin-top: ${props => props.theme.spacing(2)};
  cursor: pointer;

  input[type="checkbox"] {
    margin: 0;
  }
`;

const CallModal = ({ 
  isOpen, 
  onClose, 
  customerPhone, 
  customerName, 
  leadId, 
  onCallSuccess 
}) => {
  const [userPhone, setUserPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rememberPhone, setRememberPhone] = useState(true);

  // Load saved phone number
  useEffect(() => {
    const savedPhone = localStorage.getItem('userPhoneNumber');
    if (savedPhone) {
      setUserPhone(savedPhone);
    }
  }, []);

  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
    
    // Remove non-digits
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1);
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    } else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    return phone;
  };

  const handleCall = async () => {
    if (!userPhone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Save phone number for future use
      if (rememberPhone) {
        localStorage.setItem('userPhoneNumber', userPhone);
      }

      const result = await initiateClickToCall({
        to_number: customerPhone,
        callback_number: userPhone,
        lead_id: leadId,
        notes: notes || `Call to ${customerName || customerPhone}`
      });

      if (result.success) {
        setSuccess('Your phone will ring momentarily to connect the call.');
        
        // Notify parent component of successful call
        if (onCallSuccess) {
          onCallSuccess(result);
        }

        // Close modal after a short delay
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 3000);
      } else {
        setError(result.message || 'Failed to initiate call');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while initiating the call');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError('');
      setSuccess('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Place Call"
      hideFooter
    >
      <CallDetails>
        <CallInfo>
          <FaPhoneAlt />
          <div>
            <div>Calling:</div>
            <PhoneNumber>{formatPhoneForDisplay(customerPhone)}</PhoneNumber>
          </div>
        </CallInfo>

        {customerName && (
          <CustomerName>
            <FaUser />
            {customerName}
          </CustomerName>
        )}
      </CallDetails>

      <MaskedInput
        label="Your Phone Number"
        icon={<FaPhoneAlt />}
        mask="(999) 999-9999"
        value={userPhone}
        onChange={(e) => setUserPhone(e.target.value)}
        placeholder="Enter your phone number"
        disabled={isLoading}
      />

      <RememberChoice>
        <input
          type="checkbox"
          checked={rememberPhone}
          onChange={(e) => setRememberPhone(e.target.checked)}
          disabled={isLoading}
        />
        Remember my phone number
      </RememberChoice>

      <Input
        label="Call Notes (Optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Add any notes about this call"
        disabled={isLoading}
      />

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <ButtonContainer>
        <CallButton onClick={handleCall} disabled={isLoading || success}>
          {isLoading ? (
            <>
              <FaSpinner className="spin" />
              Initiating...
            </>
          ) : success ? (
            'Call Initiated'
          ) : (
            <>
              <FaPhoneAlt />
              Start Call
            </>
          )}
        </CallButton>

        <CancelButton onClick={handleClose} disabled={isLoading}>
          {success ? 'Close' : 'Cancel'}
        </CancelButton>
      </ButtonContainer>

      <CallFlow>
        <FlowTitle>How it works:</FlowTitle>
        <FlowStep step="1">Your phone will ring first</FlowStep>
        <FlowStep step="2">When you answer, you'll hear "Connecting your call"</FlowStep>
        <FlowStep step="3">You'll be connected to the customer</FlowStep>
        <FlowStep step="4">Call will be automatically recorded and logged</FlowStep>
      </CallFlow>
    </Modal>
  );
};

export default CallModal;